import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import BillBreakdown from "../components/BillBreakdown";
import CheckoutModal from "../components/CheckoutModal";
import PaymentReceiptModal from "../components/PaymentReceiptModal";
import RescheduleModal from "../components/RescheduleModal";
import Toast from "../components/Toast";
import { bookedForLine, statusStyle } from "../components/diary/diaryShared";
import { formatDateTime, formatTime, todayIn } from "../services/datetime";
import { apiErrorMessage } from "../services/api";
import { fetchAppointment, transitionAppointment } from "../services/bookingService";
import { getCurrentUser } from "../services/authService";
import { can } from "../services/permissions";
import { labelFor } from "../services/enumLabels";

/**
 * BIZ-14 — one appointment, and everything that can be done to it.
 *
 * The action buttons come straight from the API's `can_transition_to`. Nothing
 * here decides what is legal: the status machine owns that, and rendering its
 * answer means the buttons cannot offer a move the API would refuse.
 *
 * Cancelling asks for a reason and shows what it will cost, because the fee is
 * computed against the policy snapshotted when the booking was taken — not
 * whatever the service says today.
 */
export default function AppointmentDetail() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();

  const [appointment, setAppointment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);

  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelError, setCancelError] = useState("");
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [receiptOpen, setReceiptOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setAppointment(await fetchAppointment(id));
    } catch (err) {
      setError(apiErrorMessage(err, t("appointments.loadOneFailed")));
      setAppointment(null);
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    load();
  }, [load, i18n.language]);

  const move = async (status, reason = null) => {
    setBusy(true);
    setCancelError("");
    try {
      setAppointment(await transitionAppointment(id, status, reason));
      setToast({ type: "success", message: t(`appointments.moved.${status}`) });
      setCancelOpen(false);
      setCancelReason("");
    } catch (err) {
      const message = apiErrorMessage(err, t("appointments.moveFailed"));
      if (status === "cancelled") setCancelError(message);
      else setToast({ type: "error", message });
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <p className="py-16 text-center text-sm text-ink-2">{t("common.loading")}</p>;
  }

  if (error || !appointment) {
    return (
      <div>
        <Link to="/diary" className="text-sm font-medium text-accent-text hover:underline">
          ← {t("appointments.backToDiary")}
        </Link>
        <div className="mt-4 rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text">
          {error || t("appointments.notFound")}
        </div>
      </div>
    );
  }

  const next = appointment.can_transition_to ?? [];
  // Cancelling has its own dialog: it takes a reason and warns about the fee.
  const simpleMoves = next.filter((s) => s !== "cancelled");
  // The API's answer: false once 90 minutes have passed since the start, and
  // for any finished booking. The status check stays for older responses.
  const canReschedule =
    ["pending", "confirmed"].includes(appointment.status) && appointment.can_reschedule !== false;
  // A business cancellation needs a reason of 3–500 characters (the API's rule).
  const reasonReady = cancelReason.trim().length >= 3;
  /**
   * Two moves are time-bound on the server and they bound opposite ways, so a
   * booking that has not been dealt with is missing a different button
   * depending on where in its life it is:
   *
   * - **before it starts** — check-in has not opened and a no-show cannot
   *   honestly be recorded yet, so *both* are absent;
   * - **after check-in closes** — check-in is gone for good and no-show is
   *   the verdict that is left.
   *
   * Say which rather than leave a gap. "Check-in opens shortly" on a booking
   * that finished an hour ago would be untrue and would have reception
   * waiting for a button that is never coming.
   *
   * The *decision* stays the server's — both buttons follow
   * `can_transition_to` either way; this only labels the gap one leaves.
   *
   * Telling the two apart needs no copy of the server's minutes: "not yet"
   * means now is before the start, and the start is before the end, so a
   * withheld check-in on a booking that has already **ended** can only be the
   * late bound. `ends_at` is an absolute instant, so the comparison carries no
   * timezone of its own. A booking from an earlier day is the same case — it
   * has certainly ended — which is why the two collapse into one message.
   */
  const checkInWithheld =
    ["pending", "confirmed"].includes(appointment.status) && !next.includes("checked_in");
  const isStale =
    Boolean(appointment.local_starts_at) &&
    appointment.local_starts_at.slice(0, 10) < todayIn(appointment.timezone);
  // Gone: nobody marked it and its check-in window has closed for good.
  const hasGone =
    isStale || (Boolean(appointment.ends_at) && new Date(appointment.ends_at) <= new Date());
  // Loyalty rewards are used inside Take payment (CheckoutModal), not from a
  // separate button here; Loyalty → Redeem stays for standalone redemptions.
  const mayTakePayment = can(getCurrentUser(), "payments.create");
  const mayViewPayments = can(getCurrentUser(), "payments.view");
  // The server's answer (unpaid | partial | paid, balance_due): no money maths
  // here. Fully paid → View payment instead of Take payment; partly paid →
  // Take payment for the balance, plus View payments.
  const paymentStatus = appointment.payment_status ?? "unpaid";
  const takenPayments = appointment.payments ?? [];
  const showTakePayment = mayTakePayment && appointment.can_take_payment && paymentStatus !== "paid";
  const showViewPayments = mayViewPayments && takenPayments.length > 0;

  return (
    <div>
      <Link to="/diary" className="text-sm font-medium text-accent-text hover:underline">
        ← {t("appointments.backToDiary")}
      </Link>

      {/* Header */}
      <div className="card mt-3 mb-4 p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-bold">
                {appointment.client?.name ?? t("diary.noClient")}
              </h1>
              <span
                className={`rounded-full border px-3 py-1 text-xs font-medium ${statusStyle(appointment.status)}`}
              >
                {labelFor("booking_status", appointment.status)}
              </span>
            </div>

            {bookedForLine(t, appointment) && (
              <p className="mt-1 text-sm font-medium text-accent-text">{bookedForLine(t, appointment)}</p>
            )}

            <p className="mt-1 text-sm text-ink-2">
              <bdi>
                {formatDateTime(appointment.local_starts_at, i18n.language)} –{" "}
                {formatTime(appointment.local_ends_at)}
              </bdi>
              <span className="ms-2 text-xs text-ink-2">({appointment.timezone})</span>
            </p>

            <p className="mt-1 text-xs text-ink-2">
              {t("appointments.reference")}: <span dir="ltr">{appointment.reference}</span>
              {appointment.branch && <> · {appointment.branch.name}</>}
              {" · "}
              {labelFor("booking_source", appointment.source)}
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            {simpleMoves.map((status) => (
              <button
                key={status}
                type="button"
                disabled={busy}
                onClick={() => move(status)}
                className={status === "no_show" ? "btn-secondary" : "btn-primary"}
              >
                {t(`appointments.action.${status}`)}
              </button>
            ))}

            {canReschedule && (
              <button
                type="button"
                className="btn-secondary"
                disabled={busy}
                onClick={() => setRescheduleOpen(true)}
              >
                {t("appointments.reschedule")}
              </button>
            )}

            {/* BIZ-17, narrowed by OUP-32 and OUP-37: the till opens only once
                the visit is completed, and never for a no-show. `can_take_payment`
                is the server's own answer, so the button is absent for exactly
                the bookings the API would refuse. */}
            {showTakePayment && (
              <button type="button" className="btn-primary" onClick={() => setCheckoutOpen(true)}>
                {paymentStatus === "partial" ? (
                  <>
                    {t("checkout.openBalance")}{" "}
                    <bdi dir="ltr" className="tabular-nums">
                      {appointment.balance_due} {appointment.currency?.code ?? ""}
                    </bdi>
                  </>
                ) : (
                  t("checkout.open")
                )}
              </button>
            )}

            {showViewPayments && (
              <button
                type="button"
                className={paymentStatus === "paid" ? "btn-primary" : "btn-secondary"}
                onClick={() => setReceiptOpen(true)}
              >
                {t(takenPayments.length > 1 ? "checkout.viewPayments" : "checkout.viewPayment")}
              </button>
            )}

            {paymentStatus !== "unpaid" && (
              <span
                className={`rounded-full border px-3 py-1 text-xs font-medium ${
                  paymentStatus === "paid"
                    ? "border-st-completed bg-st-completed-wash text-st-completed-text"
                    : "border-warning-line bg-warning-wash text-warning-text"
                }`}
              >
                {labelFor("checkout_payment_status", paymentStatus)}
              </span>
            )}

            {checkInWithheld && (
              <p className="basis-full text-xs text-ink-2">
                {t(hasGone ? "appointments.checkInGone" : "appointments.checkInNotYet")}
              </p>
            )}

            {next.includes("cancelled") && (
              <button
                type="button"
                className="btn-secondary text-danger-text"
                disabled={busy}
                onClick={() => {
                  setCancelError("");
                  setCancelOpen(true);
                }}
              >
                {t("appointments.action.cancelled")}
              </button>
            )}
          </div>
        </div>

        {next.length === 0 && (
          <p className="mt-3 rounded-lg bg-raised px-3 py-2 text-xs text-ink-2">
            {t("appointments.terminal")}
          </p>
        )}
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        {/* Services */}
        <div className="card p-5 lg:col-span-2">
          <h2 className="mb-3 text-sm font-semibold text-ink">
            {t("appointments.services")}
          </h2>

          <div className="overflow-x-auto">
            <table className="w-full text-start text-sm">
              <thead className="border-b border-line text-xs uppercase tracking-wide text-ink-2">
                <tr>
                  <th className="py-2">{t("appointments.service")}</th>
                  <th className="py-2">{t("appointments.provider")}</th>
                  <th className="py-2">{t("appointments.time")}</th>
                  <th className="py-2 text-end">{t("appointments.price")}</th>
                </tr>
              </thead>
              <tbody>
                {(appointment.services ?? []).map((line) => (
                  <tr key={line.id} className="border-b border-line last:border-0">
                    <td className="py-2">
                      <p className="font-medium">{line.service?.name}</p>
                      <p className="text-xs text-ink-2">
                        {t("appointments.minutes", { count: line.duration })}
                        {(line.buffer_before > 0 || line.buffer_after > 0) && (
                          <> · {t("appointments.buffers", {
                            before: line.buffer_before,
                            after: line.buffer_after,
                          })}</>
                        )}
                      </p>
                    </td>
                    <td className="py-2">
                      {line.staff ? (
                        line.staff.display_name
                      ) : (
                        <span className="text-xs text-warning-text">
                          {t("appointments.unassigned")}
                        </span>
                      )}
                    </td>
                    <td className="py-2" dir="ltr">
                      {formatTime(line.local_starts_at)}
                    </td>
                    <td className="py-2 text-end">
                      {line.price}
                      {/* The tax this line was booked with, when added on top. */}
                      {Number(line.tax_amount) > 0 && (
                        <span className="block text-caption text-ink-2" dir="ltr">
                          + {line.tax_amount} ({Number(line.tax_rate)}%)
                        </span>
                      )}
                      {line.price_overridden && (
                        <span className="ms-1 text-caption text-warning-text">
                          {t("appointments.overridden")}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {appointment.notes && (
            <div className="mt-4 rounded-lg bg-raised p-3">
              <p className="text-xs font-medium text-ink-2">{t("appointments.notes")}</p>
              <p className="mt-1 text-sm text-ink">{appointment.notes}</p>
            </div>
          )}
        </div>

        {/* Money and policy */}
        <div className="space-y-4">
          <div className="card p-5">
            <h2 className="mb-3 text-sm font-semibold text-ink">
              {t("appointments.totals")}
            </h2>
            {/* The server's bill: the booking snapshot, less any reward used. */}
            <BillBreakdown
              bill={
                appointment.bill ?? {
                  subtotal: appointment.subtotal,
                  discount: "0.00",
                  tax_amount: appointment.tax_total,
                  total: appointment.total,
                  tax_rate: null,
                }
              }
              currency={appointment.currency}
            />
            <p className="mt-2 text-caption text-ink-2">{t("appointments.snapshotHint")}</p>
          </div>

          <div className="card p-5">
            <h2 className="mb-2 text-sm font-semibold text-ink">
              {t("appointments.cancellation")}
            </h2>

            {appointment.cancellation_window_hours == null ? (
              <p className="text-sm text-ink-2">{t("appointments.noCancellationPolicy")}</p>
            ) : (
              <p className="text-sm text-ink-2">
                {t("appointments.cancellationRule", {
                  hours: appointment.cancellation_window_hours,
                  percent: appointment.cancellation_fee_percent,
                })}
              </p>
            )}

            {appointment.inside_cancellation_window && (
              <p className="mt-2 rounded-lg border border-warning-line bg-warning-wash px-2 py-1 text-xs text-warning-text">
                {t("appointments.feeNow", { fee: appointment.cancellation_fee_now })}
              </p>
            )}

            {appointment.cancellation_policy && (
              <p className="mt-2 text-xs text-ink-2">{appointment.cancellation_policy}</p>
            )}

            {appointment.cancellation_reason && (
              <p className="mt-2 text-xs text-ink-2">
                {t("appointments.cancelledBecause", { reason: appointment.cancellation_reason })}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Cancelling takes a reason and shows what it will cost, so it gets its
          own dialog rather than the generic confirm. */}
      {cancelOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="card w-full max-w-md p-5">
            <h2 className="text-lg font-bold">{t("appointments.cancelTitle")}</h2>

            <p className="mt-2 text-sm text-ink-2">
              {appointment.inside_cancellation_window
                ? t("appointments.cancelInsideWindow", {
                    fee: appointment.cancellation_fee_now,
                  })
                : t("appointments.cancelMessage")}
            </p>

            <div className="mt-3">
              <label className="field-label" htmlFor="cancel-reason">
                {t("appointments.cancelReason")}
                <span className="text-danger-text" aria-hidden="true">
                  {" "}
                  *
                </span>
              </label>
              <textarea
                id="cancel-reason"
                rows={2}
                required
                minLength={3}
                maxLength={500}
                aria-required="true"
                aria-describedby="cancel-reason-help"
                className="field-input"
                placeholder={t("appointments.cancelReasonPlaceholder")}
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
              />
              <p id="cancel-reason-help" className="mt-1 text-caption text-ink-2">
                {t("appointments.cancelReasonHelp")}
              </p>
            </div>

            {cancelError && <p className="field-error mt-2">{cancelError}</p>}

            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                className="btn-secondary"
                disabled={busy}
                onClick={() => setCancelOpen(false)}
              >
                {t("appointments.keepBooking")}
              </button>
              <button
                type="button"
                className="btn-primary bg-danger hover:bg-danger-text"
                disabled={busy || !reasonReady}
                onClick={() => move("cancelled", cancelReason.trim())}
              >
                {busy ? t("common.saving") : t("appointments.action.cancelled")}
              </button>
            </div>
          </div>
        </div>
      )}

      <RescheduleModal
        open={rescheduleOpen}
        appointment={appointment}
        onClose={() => setRescheduleOpen(false)}
        onRescheduled={(updated) => {
          setAppointment(updated);
          setToast({ type: "success", message: t("appointments.rescheduled") });
        }}
      />

      <PaymentReceiptModal
        open={receiptOpen}
        payments={takenPayments}
        currency={appointment.currency}
        onClose={() => setReceiptOpen(false)}
      />

      <CheckoutModal
        appointment={appointment}
        open={checkoutOpen}
        onClose={() => setCheckoutOpen(false)}
        onPaid={(result) => {
          setToast({
            type: "success",
            message: result.payment
              ? t("checkout.taken", { amount: result.payment.amount })
              : t("checkout.started"),
          });
          load();
        }}
      />

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
}
